import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { authApi } from '../api/auth';
import { configApi } from '../api/config';
import { ApiError } from '../api/client';
import { Checkbox, Field, FormError } from '../components/Field';
import AuthLayout from '../components/AuthLayout';
import { GoogleButton } from '../components/GoogleButton';
import { acquisitionForSignUp, clearAcquisition, track } from '../lib/tracking';
import { useHasAccount } from '../state/authStore';

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const hasAccount = useHasAccount();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!accepted) {
      setError(t('auth.acceptTermsRequired'));
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await authApi.register({
        email,
        password,
        displayName: displayName || null,
        language: i18n.resolvedLanguage === 'en' ? 'En' : 'Bg',
        acceptedTerms: accepted,
        // Null unless the visitor agreed to analytics — see lib/tracking.ts.
        acquisition: acquisitionForSignUp(),
      });

      // The conversion the ads are bought for. A no-op for anyone who did not consent.
      track('sign_up', { method: 'email' });
      clearAcquisition();
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  // Somebody with an account, signed into it, has nothing to do here — and what they could do was
  // the problem: "Continue with Google" on this page finishes in the onboarding wizard, which for
  // an existing account looked like being asked for everything again. A demo visitor is the
  // exception; this page is exactly where the demo banner sends them.
  if (hasAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  if (sent) {
    return (
      <AuthLayout>
        <section className="card card--narrow">
          <h1>{t('auth.checkYourEmail')}</h1>
          <p className="muted">{t('auth.confirmationSent')}</p>
          <Link className="btn btn--primary btn--block" to="/login">
            {t('auth.signIn')}
          </Link>
        </section>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <section className="card card--narrow">
        <h1>{t('auth.signUp')}</h1>
        <p className="muted auth__lead">{t('auth.registerLead')}</p>

        {config?.googleEnabled && (
          <>
            <GoogleButton href={authApi.googleStartUrl('/onboarding')} />
            <p className="or muted">{t('auth.or')}</p>
          </>
        )}

        <form onSubmit={submit}>
          <Field
            label={`${t('auth.displayName')} (${t('common.optional')})`}
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Field
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label={t('auth.password')}
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {/* Consent has to point at something the person can actually read before they agree to
              it. This named only the privacy policy for a while, because the terms did not exist
              and an acceptance of nothing is not a defensible one. They exist now, both are linked,
              and both open in a new tab so nobody loses a half-filled form to read them. */}
          <Checkbox
            label={t('auth.acceptTerms')}
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <p className="field__hint">
            <Link to="/terms" target="_blank" rel="noreferrer">
              {t('terms.title')}
            </Link>
            {' · '}
            <Link to="/privacy" target="_blank" rel="noreferrer">
              {t('privacy.title')}
            </Link>
          </p>

          <FormError message={error} />

          <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
            {busy ? t('common.loading') : t('auth.signUp')}
          </button>
        </form>

        <p className="muted auth__switch">
          {t('auth.haveAccount')} <Link to="/login">{t('auth.signIn')}</Link>
        </p>
      </section>
    </AuthLayout>
  );
}
